/**
 * Promise wrappers for the Office compose-item APIs (Office.MessageCompose).
 *
 * The Office.js APIs are callback-based — these wrappers let the task pane
 * ``await`` body / subject / recipient reads and writes without nesting
 * callback chains.
 */

/** Read the compose body as HTML; rejects on Office.AsyncResult failure. */
export function getBodyHtml(item) {
  return new Promise((resolve, reject) =>
    item.body.getAsync(Office.CoercionType.Html, (r) =>
      r.status === Office.AsyncResultStatus.Succeeded ? resolve(r.value || "") : reject(r.error),
    ),
  );
}

/** Replace the compose body with the given HTML; rejects on failure. */
export function setBodyHtml(item, html) {
  return new Promise((resolve, reject) =>
    item.body.setAsync(html, { coercionType: Office.CoercionType.Html }, (r) =>
      r.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(r.error),
    ),
  );
}

/**
 * Insert HTML at the current cursor position (or replace the active selection)
 * in the compose body, leaving the rest of the body untouched; rejects on
 * failure. Office places the content at the caret's last known position in the
 * body — used to drop the secure-link block where the user is typing.
 */
export function setSelectedHtml(item, html) {
  return new Promise((resolve, reject) =>
    item.body.setSelectedDataAsync(html, { coercionType: Office.CoercionType.Html }, (r) =>
      r.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(r.error),
    ),
  );
}

/** Read the compose subject; resolves to "" on failure (defensive — no caller blocks on it). */
export function getSubject(item) {
  return new Promise((resolve) =>
    item.subject.getAsync((r) =>
      resolve(r.status === Office.AsyncResultStatus.Succeeded ? r.value || "" : ""),
    ),
  );
}

/** Collect To + Cc + Bcc addresses (lowercased + deduped); missing fields and failures contribute none. */
export function getAllRecipients(item) {
  const collect = (field) =>
    new Promise((resolve) => {
      if (!field) return resolve([]);
      field.getAsync((r) =>
        resolve(
          r.status === Office.AsyncResultStatus.Succeeded
            ? r.value.map((rcpt) => rcpt.emailAddress).filter(Boolean)
            : [],
        ),
      );
    });
  return Promise.all([collect(item.to), collect(item.cc), collect(item.bcc)]).then((groups) => {
    const seen = new Set();
    for (const email of groups.flat()) seen.add(email.trim().toLowerCase());
    return [...seen];
  });
}

/**
 * The draft's own file attachments — what Outlook holds, not what the pane staged.
 * Only real files, and only non-inline ones: an attached mail item or appointment
 * comes back as .eml/.icalendar rather than bytes, a cloud attachment is just a
 * link, and inline images are the signature. Allow-listing the one movable type
 * (rather than excluding the known-bad ones) keeps anything Outlook adds later out
 * of a total the caller promises it can move. Resolves to [] on failure — the
 * caller only uses this to decide whether to suggest LeapFILE.
 */
export function getFileAttachments(item) {
  return new Promise((resolve) =>
    item.getAttachmentsAsync((r) =>
      resolve(
        r.status === Office.AsyncResultStatus.Succeeded
          ? (r.value || []).filter(
              (a) => !a.isInline && a.attachmentType === Office.MailboxEnums.AttachmentType.File,
            )
          : [],
      ),
    ),
  );
}

/**
 * Read one attachment's bytes back out of the draft as a File, ready for the upload
 * queue. Outlook hands file attachments back base64-encoded; anything else (an
 * attached mail item, a calendar invite, a cloud link) has no bytes to upload and
 * rejects. Rejects on Office.AsyncResult failure too.
 */
export function getAttachmentFile(item, details) {
  return new Promise((resolve, reject) =>
    item.getAttachmentContentAsync(details.id, (r) => {
      if (r.status !== Office.AsyncResultStatus.Succeeded) return reject(r.error);
      const content = r.value || {};
      if (content.format !== Office.MailboxEnums.AttachmentContentFormat.Base64) {
        return reject(new Error(`Attachment is not a file (${content.format}).`));
      }
      const binary = atob(content.content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      resolve(new File([bytes], details.name));
    }),
  );
}

/** Drop one attachment from the draft; rejects on failure. */
export function removeAttachment(item, id) {
  return new Promise((resolve, reject) =>
    item.removeAttachmentAsync(id, (r) =>
      r.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(r.error),
    ),
  );
}
